/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useRef, useState } from 'react';

import cookies from 'js/lib/cookie';

import { Grid, IconButton, PageGridContainer, breakpoints } from '@coursera/cds-core';

import TrackedButton from 'bundles/page/components/TrackedButton';
import TrackedDiv from 'bundles/page/components/TrackedDiv';

import _t from 'i18n!nls/unified-common';

type Props = {
  message?: React.ReactNode;
  bannerId: string;
  hasPersistentDismiss?: boolean;
};

const styles = {
  container: (height: string) => css`
    font-size: 1.1rem;
    background: var(--cds-color-blue-50);
    transition: all 0.3s ease-in-out;
    overflow: hidden;
    height: ${height};
  `,
  hideBanner: css`
    height: 0;
    visibility: hidden;
  `,
  content: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--cds-spacing-100) 0;

    ${breakpoints.down('xs')} {
      justify-content: left;
    }
  `,
  message: css`
    margin-right: var(--cds-spacing-400);
    flex-grow: 1;
    text-align: center;
  `,
  dismissOffset: css`
    width: 36px;
  `,
};

const PageBanner = ({ message, bannerId, hasPersistentDismiss }: Props) => {
  const cookieName = `alert_closed_${bannerId}`;
  // Using useState here to cache the value to allow animation when closing with persistence.
  const [intialCookieDismissedValue] = useState(() => cookies.get(cookieName) === 'true');

  const [hideBanner, setBannerState] = useState<boolean>(hasPersistentDismiss ? intialCookieDismissedValue : false);
  const [height, setHeight] = useState<string>('auto');
  const containerRef = useRef<HTMLDivElement>(null);

  // Invalid prop escape path
  if (!message) {
    return null;
  }

  // Cookie based dismiss escape path
  if (hasPersistentDismiss && intialCookieDismissedValue) {
    return null;
  }

  const onDismiss = () => {
    if (containerRef.current) {
      setHeight(`${containerRef.current.clientHeight}px`);
    }

    // We need to set the height to the current height so that we can animate the height when hiding the banner
    setTimeout(() => {
      if (hasPersistentDismiss) {
        cookies.set(cookieName, 'true');
      }

      setBannerState(true);
    }, 0);
  };

  return (
    <div css={[styles.container(height), hideBanner ? styles.hideBanner : []]} ref={containerRef}>
      <TrackedDiv trackingName={bannerId} trackClicks={false} withVisibilityTracking>
        <PageGridContainer>
          <Grid item xs={12}>
            <div css={styles.content}>
              <div css={styles.dismissOffset} />
              <div css={styles.message}>{message}</div>
              <IconButton
                aria-label={_t('Close banner')}
                intent="close"
                trackingName={`${bannerId}_close`}
                variant="ghost"
                size="small"
                component={TrackedButton}
                onClick={onDismiss}
                withVisibilityTracking={false}
                requireFullyVisible={false}
              />
            </div>
          </Grid>
        </PageGridContainer>
      </TrackedDiv>
    </div>
  );
};

export default PageBanner;
